import {Inject, Injectable} from '@angular/core';
import {ToastService} from './toast.service';
import {LoadingService} from './loading.service';
import {AlignmentPosition, CutPaperAction, Emulation, RasterObj, StarPRNT} from '@awesome-cordova-plugins/star-prnt/ngx';
import {BehaviorSubject, Observable} from 'rxjs';
import {CashSession} from '../_models/cash-information/cash-session';
import {CashSessionSummary} from '../_models/cash-information/cash-session-summary';
import {Organization} from '../_models/organization';
import {Timecard} from '../_models/timecard';
import {Order} from '../_models/order';
import {formattedDate, formatToHoursAndMinutes} from '../_utils/date-utils';
import {buildGiftReceiptHeader, buildLineItem, buildOrderReceipt, lineSeparator} from '../_utils/receipt-utils';
import {OrderItem} from '../_models/order-item';
import {CurrencyPipe, DatePipe, formatDate} from '@angular/common';
import {Availabilities} from '../_constants/availabilities';
import {Tab} from '../_models/tab';
import {LibConfig, LibConfigService} from '../lib.config';
import moment from 'moment/moment';
import {GiftCard} from '../_models/gift-card';
import {WifiPrinter} from '../_models/wifi-printer';
import {WifiPrinterService} from './wifi-printer.service';
import {Membership} from '../_models/membership';
import {OrganizationService} from './organization.service';

@Injectable({
    providedIn: 'root'
})
export class StarPrinterService {
    port: string;
    modelName: string;
    columns;
    currentWifiPrinter: WifiPrinter;
    currentOrganization: Organization;

    public connected: Observable<string>;
    private connectedSubject: BehaviorSubject<string>;

    constructor(
        @Inject(LibConfigService) private config: LibConfig,
        private loadingService: LoadingService,
        private toastService: ToastService,
        private wifiPrinterService: WifiPrinterService,
        private organizationService: OrganizationService,
        private currencyPipe: CurrencyPipe,
        private datePipe: DatePipe,
        private starPRNT: StarPRNT
    ) {
        this.connectedSubject = new BehaviorSubject<string>(null);
        this.connected = this.connectedSubject.asObservable();

        const col = localStorage.getItem('PRINTER_COLUMNS');
        this.columns = !!col ? parseInt(col, 10) : 38;
        this.wifiPrinterService.current.subscribe(w => {
            this.currentWifiPrinter = !!w && w.type === 'STAR' ? new WifiPrinter(w) : null;
            if (!!this.currentWifiPrinter) {
                this.connectedSubject.next('WIFI');
            }
        });

        this.organizationService.current.subscribe(o => this.currentOrganization = !!o ? new Organization(o) : null);
    }

    async discover() {
        try {
            const res = await this.starPRNT.portDiscovery('All');
            const printer = (!!res && res.length > 0) ? res.find(p => !!p.portName && p.portName.startsWith('BT:')) : null;

            if (!!printer) {
                this.port = printer.portName;
                this.modelName = printer.modelName;
            } else {
                this.port = null;
                this.columns = null;
            }
        } catch (error) {
            console.error(error);
        }

        if (!!this.port) {
            this.connectedSubject.next('BLUETOOTH');
        }
    }

    async initializeWifiPrinter(wifiPrinter: WifiPrinter) {
        if (!!wifiPrinter) {
            return this.connect(wifiPrinter, true);
        }
    }

    async connect(wifiPrinter: WifiPrinter, silent: boolean): Promise<boolean> {
        if (!silent) {
            this.loadingService.present('Connecting printer.');
        }

        return new Promise((resolve) => {
            try {
                this.starPRNT.connect(`TCP:${wifiPrinter.ipAddress}`, wifiPrinter.emulation, false).subscribe(c => {
                    console.log(`Connected on ${`TCP:${wifiPrinter.ipAddress}`}`);
                    resolve(true);
                }, error => {
                    console.log(error);
                    if (error === 'cordova_not_available') {
                        this.toastService.error('Star Printers can only be connected via tablet.');
                    } else if (!silent) {
                        this.loadingService.dismiss();
                        this.toastService.error(`Could not connect to printer at ${wifiPrinter.ipAddress}.`);
                    }
                    console.log(`Failed connection on ${wifiPrinter.ipAddress}`);
                    resolve(false);
                }, () => this.loadingService.dismiss());
            } catch (error) {
                if (!silent) {
                    this.loadingService.dismiss();
                    this.toastService.error(`Could not connect to printer at ${wifiPrinter.ipAddress}.`);
                }
                resolve(false);
            }
        });
    }

    async openDrawer() {
        if (!!this.getPort()) {
            await this.starPRNT.openCashDrawer(this.getPort(), this.getEmulation());
        }
    }

    async print(data): Promise<boolean> {
        const port = this.getPort();
        const emulation = this.getEmulation();
        return new Promise(async (resolve) => {
            if (!!port) {
                try {
                    if (emulation === Emulation.StarGraphic) {
                        const rasterObj: RasterObj = {
                            text: data,
                            paperWidth: 576,
                            fontSize: 25,
                            cutReceipt: true,
                            openCashDrawer: false
                        };

                        this.starPRNT.printRasterReceipt(port, emulation, rasterObj)
                            .then((c) => console.log(c))
                            .catch(error => {
                                this.toastService.error(error);
                            });
                        resolve(true);
                    } else {
                        const commandsArray = [];
                        commandsArray.push({appendAlignment: AlignmentPosition.Center});
                        commandsArray.push({appendLogo: 1});
                        commandsArray.push({appendAlignment: AlignmentPosition.Left});
                        commandsArray.push({append: data});

                        this.send(null, commandsArray, true);
                        resolve(true);
                    }
                } catch (error) {
                    await this.toastService.error(error);
                    resolve(false);
                }
            } else {
                await this.toastService.error('No printer found.');
                resolve(false);
            }
        });
    }

    printGiftCardBalances(organization: Organization, giftCards: GiftCard[]) {
        try {
            giftCards.forEach(giftCard => {
                const commandsArray = [];
                commandsArray.push({appendAlignment: AlignmentPosition.Center});
                commandsArray.push({appendLogo: 1});
                commandsArray.push({appendAlignment: AlignmentPosition.Left});
                commandsArray.push({append: buildGiftReceiptHeader(organization, this.columns)});
                commandsArray.push({appendAlignment: AlignmentPosition.Center});
                commandsArray.push({appendMultiple: `\nBalance: ${giftCard.cardNumber}\n\n`, width: 2, height: 2});
                commandsArray.push({
                    appendMultiple: `\nBalance: ${this.currencyPipe.transform(giftCard.balance)}\n\n`,
                    width: 2,
                    height: 2
                });

                this.send(null, commandsArray);
            });
        } catch (error) {
            console.log(error);
            this.loadingService.dismiss();
            this.toastService.error('Could not print summary');
        }
    }

    printClaimTab(tab: Tab) {
        try {
            const commandsArray = [];
            commandsArray.push({appendAlignment: AlignmentPosition.Center});
            commandsArray.push({appendLogo: 1});
            commandsArray.push({appendMultiple: `\nThank you ${tab.personName}!\n\n`, width: 2, height: 2});
            commandsArray.push({append: `Scan the QR code to manage your tab from your phone.\n\n`});
            commandsArray.push({
                appendQrCode: `${this.config.mobileUrl}/#/claim-tab/${tab.id}/${tab.claimCode}`,
                QrCodeModel: 'No2',
                QrCodeLevel: 'M',
                cell: 8,
                alignment: 'Center'
            });

            if (!!this.currentOrganization && !!this.currentOrganization.claimTabReceiptTag) {
                commandsArray.push(`\n\n${this.currentOrganization.claimTabReceiptTag}\n\n`);
            }

            this.send(null, commandsArray);
        } catch (error) {
            console.log(error);
            this.loadingService.dismiss();
            this.toastService.error('Could not print summary');
        }
    }

    printClaimMembership(member: Membership) {
        try {
            const commandsArray = [];
            commandsArray.push({appendAlignment: AlignmentPosition.Center});
            commandsArray.push({appendLogo: 1});
            commandsArray.push({appendMultiple: `\nThank you ${!!member.person ? member.person.name : ''}!\n\n`, width: 2, height: 2});
            commandsArray.push({append: `Scan the QR code to manage your membership from your phone.\n\n`});
            commandsArray.push({
                appendQrCode: `${this.config.mobileUrl}/#/${member.orgId}/claim-membership/${member.id}?claimCode=${member.claimCode}`,
                QrCodeModel: 'No2',
                QrCodeLevel: 'M',
                cell: 8,
                alignment: 'Center'
            });

            this.send(null, commandsArray);
        } catch (error) {
            console.log(error);
            this.loadingService.dismiss();
            this.toastService.error('Could not print summary');
        }
    }

    printCashSession(cashSession: CashSession, summary: CashSessionSummary, organization: Organization) {
        this.loadingService.present('Printing.');
        try {
            const commandsArray = [];
            commandsArray.push({append: organization.name + '\n'});

            commandsArray.push({appendAlignment: AlignmentPosition.Right});
            commandsArray.push({append: `${formattedDate(cashSession.pendedTime)}\n`});

            this.addSeparator(commandsArray);

            commandsArray.push({appendAlignment: AlignmentPosition.Left});
            commandsArray.push({append: '\nSales\n'});
            this.addSeparator(commandsArray);

            commandsArray.push({appendAlignment: AlignmentPosition.Left});
            commandsArray.push({
                append: buildLineItem('Cash:',
                    this.currencyPipe.transform(summary.cashSalesNet), 0, this.columns)
            });
            commandsArray.push({
                append: buildLineItem('Charge:',
                    this.currencyPipe.transform(summary.chargePrincipal), 0, this.columns)
            });
            commandsArray.push({
                append: buildLineItem('Gift Card:',
                    this.currencyPipe.transform(summary.giftCardPrincipal), 0, this.columns)
            });
            commandsArray.push({
                append: buildLineItem('Comps:',
                    this.currencyPipe.transform(summary.creditAmount), 0, this.columns)
            });
            commandsArray.push({
                append: buildLineItem('Total:',
                    this.currencyPipe.transform(summary.total), 0, this.columns)
            });
            this.addSeparator(commandsArray);

            commandsArray.push({appendAlignment: AlignmentPosition.Left});
            commandsArray.push({append: '\nTips\n'});
            this.addSeparator(commandsArray);

            commandsArray.push({appendAlignment: AlignmentPosition.Left});
            commandsArray.push({
                append: buildLineItem('Cash:',
                    this.currencyPipe.transform(summary.cashTipIn), 0, this.columns)
            });
            commandsArray.push({
                append: buildLineItem('Charge:',
                    this.currencyPipe.transform(summary.chargeTip), 0, this.columns)
            });
            commandsArray.push({
                append: buildLineItem('Gift Card:',
                    this.currencyPipe.transform(summary.giftCardTip), 0, this.columns)
            });
            commandsArray.push({
                append: buildLineItem('Adjustments:',
                    this.currencyPipe.transform(summary.tipAdjustments), 0, this.columns)
            });
            commandsArray.push({
                append: buildLineItem('Total:',
                    this.currencyPipe.transform(summary.tipTotal), 0, this.columns)
            });
            this.addSeparator(commandsArray);

            commandsArray.push({
                append: buildLineItem('Transfers:',
                    this.currencyPipe.transform(summary.transfers), 0, this.columns)
            });
            this.addSeparator(commandsArray);

            if (!!cashSession.cashDrawers && cashSession.cashDrawers.length > 0) {
                cashSession.cashDrawers.forEach(d => {
                    commandsArray.push({append: `\n${d.name}\n`});
                    this.addSeparator(commandsArray);

                    commandsArray.push({
                        append: buildLineItem('Final Count:',
                            this.currencyPipe.transform(d.endAmount), 0, this.columns)
                    });

                    if (d.ones) {
                        commandsArray.push({
                            append: buildLineItem('Ones:',
                                d.ones.toString(10), 0, this.columns)
                        });
                    }
                    if (d.fives) {
                        commandsArray.push({
                            append: buildLineItem('Fives:',
                                d.fives.toString(10), 0, this.columns)
                        });
                    }
                    if (d.tens) {
                        commandsArray.push({
                            append: buildLineItem('Tens:',
                                d.tens.toString(10), 0, this.columns)
                        });
                    }
                    if (d.twenties) {
                        commandsArray.push({
                            append: buildLineItem('Twenties:',
                                d.twenties.toString(10), 0, this.columns)
                        });
                    }
                    if (d.fifties) {
                        commandsArray.push({
                            append: buildLineItem('Fifties:',
                                d.fifties.toString(10), 0, this.columns)
                        });
                    }
                    if (d.hundreds) {
                        commandsArray.push({
                            append: buildLineItem('Hundreds:',
                                d.hundreds.toString(10), 0, this.columns)
                        });
                    }
                    if (d.pennies) {
                        commandsArray.push({
                            append: buildLineItem('Pennies:',
                                d.pennies.toString(10), 0, this.columns)
                        });
                    }
                    if (d.nickels) {
                        commandsArray.push({
                            append: buildLineItem('Nickels:',
                                d.nickels.toString(10), 0, this.columns)
                        });
                    }
                    if (d.dimes) {
                        commandsArray.push({
                            append: buildLineItem('Dimes:',
                                d.dimes.toString(10), 0, this.columns)
                        });
                    }
                    if (d.quarters) {
                        commandsArray.push({
                            append: buildLineItem('Quarters:',
                                d.quarters.toString(10), 0, this.columns)
                        });
                    }
                    this.addSeparator(commandsArray);
                });
            }

            commandsArray.push({appendAlignment: AlignmentPosition.Left});
            if (!!cashSession.notes) {
                cashSession.notes.forEach(note => {
                    commandsArray.push({
                        append: buildLineItem(note.createdBy.name,
                            this.datePipe.transform(note.createDate), 0, this.columns)
                    });
                    commandsArray.push({
                        append: note.note + '\n'
                    });
                    this.addSeparator(commandsArray);
                });
            }

            this.send(null, commandsArray);
        } catch (error) {
            console.log(error);
            this.loadingService.dismiss();
            this.toastService.error('Could not print summary');
        }
        this.loadingService.dismiss();
    }

    printTimeCard(timecard: Timecard, organization: Organization) {
        this.loadingService.present('Printing.');
        try {
            const commandsArray = [];
            commandsArray.push({appendAlignment: AlignmentPosition.Left});
            commandsArray.push({append: organization.name + '\n'});
            commandsArray.push({append: timecard.person.name + '\n'});
            this.addSeparator(commandsArray);
            commandsArray.push({appendAlignment: AlignmentPosition.Left});
            commandsArray.push({
                append: buildLineItem('Clock In:',
                    formatDate(timecard.clockInTime, 'M/d/yy h:mm a', 'en-US'), 0, this.columns)
            });

            if (!!timecard.clockOutTime) {
                commandsArray.push({
                    append: buildLineItem('Clock Out:',
                        formatDate(timecard.clockOutTime, 'M/d/yy h:mm a', 'en-US'), 0, this.columns)
                });

                commandsArray.push({
                    append: buildLineItem('Duration:',
                        formatToHoursAndMinutes(
                            moment.duration(+new Date(timecard.clockOutTime) - +new Date(timecard.clockInTime))), 0, this.columns)
                });

                if (!!timecard.cashTips) {
                    commandsArray.push({
                        append: buildLineItem('Cash Tips:',
                            this.currencyPipe.transform(timecard.cashTips), 0, this.columns)
                    });
                }

                if (!!timecard.payrollTips) {
                    commandsArray.push({
                        append: buildLineItem('Payroll Tips:',
                            this.currencyPipe.transform(timecard.payrollTips), 0, this.columns)
                    });
                }
            }

            this.send(null, commandsArray);
        } catch (error) {
            console.log(error);
            this.loadingService.dismiss();
            this.toastService.error('Could not print summary');
        }
        this.loadingService.dismiss();
    }

    async printOrder(order: Order, items: OrderItem[], selectedPrinter: WifiPrinter, silent = false): Promise<boolean> {
        return new Promise(async (resolve) => {
            if (!silent) {
                this.loadingService.present('Printing order.');
            }
            try {
                const port = this.getPort(selectedPrinter);
                const emulation = this.getEmulation(selectedPrinter);
                const columns = this.getColumns(selectedPrinter);

                if (emulation === Emulation.StarGraphic) {
                    if (!!order.orderMarker) {
                        const r1: RasterObj = {
                            text: order.orderMarker.toString(10),
                            paperWidth: 576,
                            fontSize: 120,
                            cutReceipt: false,
                            openCashDrawer: false
                        };

                        await this.starPRNT.printRasterReceipt(port, emulation, r1);
                        resolve(true);
                    }
                    if (!!order.tableStandMarker) {
                        const r2: RasterObj = {
                            text: order.tableStandMarker.toString(10),
                            paperWidth: 576,
                            fontSize: 120,
                            cutReceipt: false,
                            openCashDrawer: false
                        };

                        await this.starPRNT.printRasterReceipt(port, emulation, r2);
                        resolve(true);
                    } else if (!!order.orderNumber) {
                        const r2: RasterObj = {
                            text: order.orderNumber.toString(10),
                            paperWidth: 576,
                            fontSize: 120,
                            cutReceipt: false,
                            openCashDrawer: false
                        };

                        await this.starPRNT.printRasterReceipt(port, emulation, r2);
                        resolve(true);
                    }

                    const rasterObj = {
                        text: (!order.orderMarker && !order.tableStandMarker
                            ? '\n\n\n\n'
                            : lineSeparator(columns)) + buildOrderReceipt(order, columns) + '\n\n\n\n',
                        paperWidth: 576,
                        fontSize: 25,
                        cutReceipt: true,
                        openCashDrawer: false
                    };

                    await this.starPRNT.printRasterReceipt(port, emulation, rasterObj);
                    resolve(true);
                } else {
                    const commandsArray = [];
                    commandsArray.push({appendAlignment: AlignmentPosition.Center});

                    if (!!order.orderMarker) {
                        commandsArray.push({appendMultiple: (order.orderMarker + '\n\n'), width: 5, height: 5});
                        this.addSeparator(commandsArray);
                    }

                    if (!!order.tableStandMarker) {
                        commandsArray.push({appendMultiple: (order.tableStandMarker + '\n\n'), width: 5, height: 5});
                        this.addSeparator(commandsArray);
                    }

                    commandsArray.push({appendAlignment: AlignmentPosition.Left});

                    if (!!order.notes) {
                        commandsArray.push({append: ('\n' + order.notes + '\n\n')});
                        this.addSeparator(commandsArray);
                    }

                    commandsArray.push({append: order.pickupLocation.name + '\n\n'});
                    this.addSeparator(commandsArray);

                    items.forEach(item => {
                        if (item.orderType === Availabilities.TOGO) {
                            commandsArray.push({appendMultiple: 'TOGO\n', width: 2, height: 2});
                        }
                        let msg = !!item.menuItem ? item.menuItem.name : 'Misc';
                        if (!!item.selectedPrice && item.selectedPrice.name !== 'Default') {
                            msg += ` (${item.selectedPrice.name})`;
                        }
                        commandsArray.push({appendMultiple: msg + '\n', width: 2, height: 2});

                        this.addItemDetails(commandsArray, item);

                        commandsArray.push({append: '\n'});
                    });

                    this.addSeparator(commandsArray);

                    commandsArray.push({appendAlignment: AlignmentPosition.Right});
                    commandsArray.push({append: formattedDate(order.date) + '\n'});

                    this.send(selectedPrinter, commandsArray, silent);
                    resolve(true);
                }
            } catch (error) {
                resolve(false);
                console.log(error);
                if (!silent) {
                    this.loadingService.dismiss();
                    this.toastService.error('Could not print order');
                }
            }
            if (!silent) {
                this.loadingService.dismiss();
            }
        });
    }

    send(selectedPrinter: WifiPrinter, commandsArray: any[], silent = false) {
        commandsArray.push({appendCutPaper: CutPaperAction.FullCutWithFeed});

        this.starPRNT.print(this.getPort(selectedPrinter), this.getEmulation(selectedPrinter), commandsArray)
            .then((result) => {
                console.log(result);
                if (!silent) {
                    this.loadingService.dismiss();
                }
            })
            .catch(error => {
                console.log(error);
                this.toastService.error(error);
                this.loadingService.dismiss();
            });
    }

    addItemDetails(commandsArray: any[], item: OrderItem, additionalPadding = '') {
        if (item.removedToppingsFilter().length > 0) {
            commandsArray.push({append: `${additionalPadding}  Remove: ${item.compactRemovedToppings()}\n`});
        }
        if (item.addedToppingsFilter().length > 0) {
            commandsArray.push({append: `${additionalPadding}  Add: ${item.compactAddedToppings()}\n`});
        }
        if (!!item.selections && item.selections.length > 0) {
            item.selections.forEach(selection => {
                commandsArray.push({append: `${additionalPadding}  ${selection.menuItemSelection.menuSelection.name}\n`});
                selection.selectedItems.forEach(si => {
                    commandsArray
                        .push({append: `${additionalPadding}    ${!!si.orderItem.menuItem ? si.orderItem.menuItem.name : 'Misc'}\n`});
                    this.addItemDetails(commandsArray, si.orderItem, additionalPadding + '    ');
                });
            });
        }
    }

    addSeparator(commandsArray: any[]) {
        commandsArray.push({appendAlignment: this.starPRNT.AlignmentPosition.Center});
        commandsArray.push({append: lineSeparator(this.columns)});
    }

    setColumns(col: string) {
        localStorage.setItem('PRINTER_COLUMNS', col);
        this.columns = Number.parseInt(col, 10);
    }

    getPort(selectedPrinter: WifiPrinter = null) {
        if (!!selectedPrinter) {
            return `TCP:${selectedPrinter.ipAddress}`;
        } else {
            return !!this.port ? this.port : (!!this.currentWifiPrinter ? `TCP:${this.currentWifiPrinter.ipAddress}` : null);
        }
    }

    getEmulation(selectedPrinter: WifiPrinter = null) {
        if (!!selectedPrinter) {
            return selectedPrinter.emulation;
        } else if (!!this.modelName && this.modelName.indexOf('TSP100') > -1) {
            return Emulation.StarGraphic;
        } else {
            return !!this.port ? Emulation.StarPRNT : (!!this.currentWifiPrinter ? this.currentWifiPrinter.emulation : null);
        }
    }

    getColumns(selectedPrinter: WifiPrinter = null) {
        if (!!selectedPrinter) {
            return selectedPrinter.columns;
        } else {
            return !!this.port ? this.columns : (!!this.currentWifiPrinter ? this.currentWifiPrinter.columns : null);
        }
    }
}
